import { useAuth } from "@clerk/clerk-react";
import { Box, Chip, Grid, IconButton, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";

const styles = {
  chip: {
    color: "green.main",
    textTransform: "uppercase",
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
  },
  addButton: {
    px: 2,
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
    color: "black",
    "&:hover": {
      backgroundColor: "white",
      border: "1px solid",
      color: "black",
    },
  },
  closeIcon: {
    position: "absolute",
    right: "18px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
};

type TemplateError = {
  idx: number;
  message: string;
};

interface IProps {
  open: boolean;
  emailSettingId: number;
  handleClose: (e?: CustomEvent) => void;
  templates: Template[] | null;
  setTemplates: Dispatch<SetStateAction<Template[]>>;
  onSuccess?: (templates: Template[], useOneTemplates?: boolean) => void;
  useOneTemplates?: boolean;
}

export default function EditFallbackTemplatesDrawer({
  open,
  emailSettingId,
  handleClose,
  templates,
  setTemplates,
  onSuccess,
  useOneTemplates,
}: IProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings } = useContext(OrganizationUserContext);

  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = (contentType: TemplateContentType) => {
    trackEvent("Generate Template Button Clicked", {
      Source: "VA Add Niche",
    });
    setCurrentTemplateContentType(contentType);
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
  };

  const initializeTemplates = () => {
    if (templates && templates.length > 0) {
      setModifiedTemplates(templates);
      return;
    }

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setModifiedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  const saveFallbackTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/${emailSettingId}`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates
            .map((t) => t.id)
            .filter((id): id is number => id !== undefined),
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }
        }
        setAlert(jsonMessage.message, "error");
      } else {
        setTemplates(modifiedTemplates);
        if (onSuccess) {
          onSuccess(modifiedTemplates, useOneTemplates);
        }
        onClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, open]);

  const onClose = () => {
    setModifiedTemplates([]);
    handleClose();
  };

  return (
    <>
      <Typography variant="h4" textAlign={"center"} gutterBottom>
        General Use Templates
      </Typography>
      <Typography textAlign={"center"} sx={{ fontSize: 16, mb: 3 }}>
        These templates are used in cases when a brand is pitched that doesn't
        fit any of your defined niches.
      </Typography>
      <IconButton sx={styles.closeIcon} onClick={onClose}>
        <Box component="span" className="fa-regular fa-xmark-large" />
      </IconButton>

      <Grid
        container
        justifyContent="center"
        alignItems={"center"}
        flexDirection="column"
        sx={{ mb: 2 }}
        gap={2}
      >
        <QuestionBox styles={{ width: { xs: "100%", md: "60%" } }}>
          <Grid container alignItems={"center"} gap={1} sx={{ mb: 3 }}>
            <Chip label="Outreach" sx={styles.chip} variant="outlined" />

            <Typography color="textSecondary">
              Let's setup your outreach template{" "}
            </Typography>
          </Grid>

          {modifiedTemplates[0] ? (
            <OutreachSelect
              templates={modifiedTemplates}
              handleChange={handleTemplateChange}
              filteredOutreachTemplates={filteredOutreachTemplates}
              templateFromEmailSettings={templateFromEmailSettings}
              addNewOption={() =>
                handleGenerateTemplate(TemplateContentType.OUTREACH)
              }
              templatesError={templatesError}
            />
          ) : (
            <StyledLargeButton
              onClick={() =>
                handleGenerateTemplate(TemplateContentType.OUTREACH)
              }
              disableElevation
              fullWidth
            >
              Generate Template
            </StyledLargeButton>
          )}
        </QuestionBox>

        <QuestionBox styles={{ width: { xs: "100%", md: "60%" } }}>
          <Grid container alignItems={"center"} gap={1} sx={{ mb: 3 }}>
            <Chip label="Follow-up" sx={styles.chip} variant="outlined" />

            <Typography color="textSecondary">
              Let's setup your followup templates
            </Typography>
          </Grid>

          {followupTemplates.length === 0 ? (
            <StyledLargeButton
              disableElevation
              disabled={outreachTemplates.length === 0}
              onClick={() =>
                handleGenerateTemplate(TemplateContentType.FOLLOWUP)
              }
              fullWidth
            >
              Generate Follow-up
            </StyledLargeButton>
          ) : (
            <Grid container gap={2} flexDirection={"column"}>
              {selectedFollowups && selectedFollowups?.length > 0 ? (
                selectedFollowups?.map((followup, index) => (
                  <FollowupSelect
                    followup={followup}
                    index={index}
                    filteredFollowUps={filteredFollowUps}
                    templateFromEmailSettings={templateFromEmailSettings}
                    handleTemplateChange={(e: CustomEvent, index: number) =>
                      handleTemplateChange(e, index)
                    }
                    onRemove={() => removeFollowUp(index + 1)}
                    templatesError={templatesError}
                  />
                ))
              ) : (
                <></>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Sent 4 business days after previous email
                </Typography>
              </Grid>

              {followupTemplates?.length > 0 &&
                modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                  <Grid item xs={12}>
                    <StyledLargeButton
                      variant="outlined"
                      disableElevation
                      onClick={addFollowUp}
                      styles={styles.addButton}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-add"
                        sx={{ mr: 0.5 }}
                      />{" "}
                      Follow Up
                    </StyledLargeButton>
                  </Grid>
                )}
            </Grid>
          )}
        </QuestionBox>
      </Grid>

      {!isPage(routes.onboardingVaSelfServe) && (
        <Typography>
          To keep campaigns consistent, it takes at most 7 days for your changes
          to take effect.
        </Typography>
      )}

      <Grid container justifyContent={"center"}>
        <StyledLargeButton
          loading={loading}
          disableElevation
          onClick={() => {
            saveFallbackTemplates();
          }}
        >
          Save Changes
        </StyledLargeButton>
      </Grid>
    </>
  );
}
